export const REDACTION_VERSION = 1;

const MAX_DEPTH = 8;
const MAX_STRING_BYTES = 4 * 1024;
const MAX_TOTAL_BYTES = 32 * 1024;

const SENSITIVE_KEY_RE =
  /^(password|otp|token|authorization|cookie|api[_-]?key|secret|email|phone|address|stripe[_-]?(secret|key))/i;

const ALLOWED_KEY_RE =
  /^(kind|route|status_code|duration_ms|correlation_id|user_id|level|source|env|release|service|message|occurred_at|created_at|sampling_rate|hmac_key_id|redaction_version|idempotency_key|email_hmac|ip_hmac|user_agent|payload|error_name|error_stack|resolved_stack|resolved_at|resolver_version|session_id)$/;

type Pattern = { re: RegExp; tag: string };

const VALUE_PATTERNS: Pattern[] = [
  // Email
  { re: /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi, tag: "[redacted:email]" },
  // JWT (header.payload.signature)
  { re: /eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g, tag: "[redacted:jwt]" },
  // Bearer / Authorization values
  { re: /\bBearer\s+[A-Za-z0-9._\-]{8,}/gi, tag: "[redacted:bearer]" },
  // Stripe secret keys
  { re: /\bsk_(?:test|live)_[A-Za-z0-9]{16,}\b/g, tag: "[redacted:stripe]" },
  // Stripe restricted keys
  { re: /\brk_(?:test|live)_[A-Za-z0-9]{16,}\b/g, tag: "[redacted:stripe]" },
  // Supabase service-role JWT prefix (heuristic)
  { re: /\beyJ[A-Za-z0-9_-]{40,}/g, tag: "[redacted:supabase-jwt]" },
  // Slack tokens (must precede phone/CC so numeric segments aren't consumed first)
  { re: /xox[baprs]-[A-Za-z0-9-]{10,}/g, tag: "[redacted:slack]" },
  // GitHub PATs
  { re: /ghp_[A-Za-z0-9]{20,}/g, tag: "[redacted:github]" },
  { re: /github_pat_[A-Za-z0-9_]{20,}/g, tag: "[redacted:github]" },
  // Phone numbers (E.164-ish, 10+ digits with optional +)
  { re: /\+?\d{10,15}/g, tag: "[redacted:phone]" },
  // 16-digit credit-card-ish numbers (loose; full validation unnecessary here)
  { re: /\b\d{13,19}\b/g, tag: "[redacted:cc]" },
  // US Social Security Numbers
  { re: /\b\d{3}-\d{2}-\d{4}\b/g, tag: "[redacted:ssn]" },
];

const redactString = (s: string): string => {
  let out = s;
  for (const { re, tag } of VALUE_PATTERNS) out = out.replace(re, tag);
  if (new TextEncoder().encode(out).byteLength > MAX_STRING_BYTES) {
    out = out.slice(0, MAX_STRING_BYTES) + "...[truncated]";
  }
  return out;
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const redactValue = (
  value: unknown,
  depth: number,
  totalBytesRef: { n: number },
  contextKey: string | null
): unknown => {
  if (depth > MAX_DEPTH) return "[redacted:depth]";
  if (totalBytesRef.n > MAX_TOTAL_BYTES) return "[redacted:size]";

  if (value === null || value === undefined) return value;
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (value instanceof Date) return value.toISOString();

  if (typeof value === "string") {
    // Sensitive *key* triggers full mask of value, unless allowlisted (e.g. email_hmac)
    if (
      contextKey &&
      SENSITIVE_KEY_RE.test(contextKey) &&
      !ALLOWED_KEY_RE.test(contextKey)
    ) {
      return "[redacted:sensitive]";
    }
    const redacted = redactString(value);
    totalBytesRef.n += new TextEncoder().encode(redacted).byteLength;
    return redacted;
  }

  if (Array.isArray(value)) {
    return value.map((v) => redactValue(v, depth + 1, totalBytesRef, null));
  }

  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      if (SENSITIVE_KEY_RE.test(k)) {
        // Drop entirely unless it's an allowlisted column name (e.g. email_hmac)
        if (!ALLOWED_KEY_RE.test(k)) continue;
      }
      out[k] = redactValue(v, depth + 1, totalBytesRef, k);
    }
    return out;
  }

  // Anything else (functions, symbols, errors) - string-stringify safely
  return "[redacted:unsupported]";
};

export function redact(input: unknown): Record<string, unknown> {
  const totalBytesRef = { n: 0 };
  const result = redactValue(input, 0, totalBytesRef, null);
  if (isPlainObject(result)) return result;
  return { value: result };
}

export type SafeStringifyOptions = { maxBytes?: number };

export function safeStringify(
  input: unknown,
  options: SafeStringifyOptions = {}
): string {
  const maxBytes = options.maxBytes ?? MAX_TOTAL_BYTES;
  const seen = new WeakSet<object>();
  const replacer = (_key: string, value: unknown) => {
    if (typeof value === "object" && value !== null) {
      if (seen.has(value as object)) return "[circular]";
      seen.add(value as object);
    }
    return value;
  };
  let out: string;
  try {
    out = JSON.stringify(input, replacer);
  } catch {
    out = String(input);
  }
  if (new TextEncoder().encode(out).byteLength > maxBytes) {
    return out.slice(0, maxBytes) + "...[truncated:size]";
  }
  return out;
}
